'use client';

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import {
  createRealtimeVoiceService,
  RealtimeVoiceService,
} from '@/lib/services/voice/realtimeVoice';
import {
  createRealtimeAudioPlayback,
  RealtimeAudioPlayback,
} from '@/lib/services/voice/audioPlayback';
import { logger } from '@/lib/services/logger';
import { getOpenAIAPIKey } from '@/lib/services/security/secrets';

interface VoiceContextType {
  isConnected: boolean;
  isListening: boolean;
  isSpeaking: boolean;
  transcript: string;
  response: string;
  error: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  startListening: () => Promise<void>;
  stopListening: () => void;
  sendText: (text: string) => void;
  sendSystemMessage: (text: string) => void; // For context updates like "User is now in module X"
  resumeAudioContext: () => Promise<void>;
}

const VoiceContext = createContext<VoiceContextType | null>(null);

export function useVoice() {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
}

export function VoiceProvider({ children }: { children: React.ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [error, setError] = useState<string | null>(null);

  const voiceServiceRef = useRef<RealtimeVoiceService | null>(null);
  const audioPlaybackRef = useRef<RealtimeAudioPlayback | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  const connect = useCallback(async () => {
    if (voiceServiceRef.current?.isReady) return;

    try {
      setError(null);
      const apiKey = process.env.NEXT_PUBLIC_OPENAI_API_KEY || (await getOpenAIAPIKey()) || '';

      if (!apiKey) {
        throw new Error('OpenAI API key not configured');
      }

      // Initialize Audio Playback
      const audioPlayback = createRealtimeAudioPlayback({
        onPlaybackStart: () => setIsSpeaking(true),
        onPlaybackEnd: () => setIsSpeaking(false),
        onError: (err) => logger.error('Audio playback error', err),
      });

      await audioPlayback.initialize();
      audioPlaybackRef.current = audioPlayback;

      // Initialize Voice Service
      const service = createRealtimeVoiceService(apiKey, {
        onTranscript: (text) => setTranscript(text),
        onResponse: (text) => {
          setResponse(text);
          setIsSpeaking(true);
        },
        onAudioChunk: (chunk) => {
          audioPlaybackRef.current?.queueAudioChunk(chunk);
        },
        onError: (err) => {
          logger.error('Voice service error', err);
          setError(err.message);
          setIsListening(false);
        },
        onEnd: () => setIsSpeaking(false),
      });

      await service.connect();
      voiceServiceRef.current = service;
      setIsConnected(true);
    } catch (err) {
      logger.error('Failed to connect voice service', err as Error);
      setError(err instanceof Error ? err.message : 'Failed to connect');
      setIsConnected(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    voiceServiceRef.current?.disconnect();
    voiceServiceRef.current = null;

    audioPlaybackRef.current?.stop().catch(() => {});
    audioPlaybackRef.current = null;

    setIsConnected(false);
    setIsListening(false);
    setIsSpeaking(false);
    setTranscript('');
    setResponse('');
  }, []);

  const startListening = useCallback(async () => {
    if (!voiceServiceRef.current?.isReady) {
      // Auto-connect if not connected
      await connect();
      if (!voiceServiceRef.current?.isReady) return;
    }

    // In a real implementation with VAD, we might just need to enable the mic stream here
    // For now, we'll assume the service handles VAD or we trigger it
    setIsListening(true);
    setTranscript('');
    setError(null);

    // Note: Actual audio capture (mic) logic needs to be integrated here or in the service
    // For this prototype, we rely on the service's capability or manual text fallback
    // If the service expects audio buffer appends, we need a MediaRecorder here.
    // Assuming VoiceGreeting's text-based fallback for now unless we add MediaRecorder.
  }, [connect]);

  // Resume audio context (must be called from user gesture)
  const resumeAudioContext = useCallback(async () => {
    if (audioPlaybackRef.current) {
      await audioPlaybackRef.current.resume();
    }
  }, []);

  const stopListening = useCallback(() => {
    setIsListening(false);
    // Stop recording logic would go here
  }, []);

  const sendText = useCallback((text: string) => {
    if (voiceServiceRef.current?.isReady) {
      voiceServiceRef.current.sendText(text);
      voiceServiceRef.current.requestResponse();
    }
  }, []);

  const sendSystemMessage = useCallback((text: string) => {
    // We can simulate a system message by sending text instructions as user or system role if supported
    // Or just context injection. For now, we'll append to session instructions if possible,
    // or just send a user message "Context update: ..."
    if (voiceServiceRef.current?.isReady) {
      // OpenAI Realtime API supports session updates.
      // For simplicity in this v1, we'll just send a text prompt to steer it.
      voiceServiceRef.current.sendText(`[System Context Update]: ${text}`);
    }
  }, []);

  return (
    <VoiceContext.Provider
      value={{
        isConnected,
        isListening,
        isSpeaking,
        transcript,
        response,
        error,
        connect,
        disconnect,
        startListening,
        stopListening,
        sendText,
        sendSystemMessage,
        resumeAudioContext,
      }}
    >
      {children}
    </VoiceContext.Provider>
  );
}
